import { memo, useEffect, useMemo, useRef, useId } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { cn } from "@/lib/utils";
import { ensureQuillFormatsRegistered } from "@sd/lib/quillEditorSetup";
import { richTextPlainTextLength } from "@sd/lib/richTextUtils";

ensureQuillFormatsRegistered();

function stripInlineImages(html: string): string {
  return html.replace(/<img\b[^>]*>/gi, "");
}

/** Static toolbar — must not re-render on every editor value change or Quill loses format state. */
const TicketDescriptionEditorToolbar = memo(function TicketDescriptionEditorToolbar({
  toolbarId,
}: {
  toolbarId: string;
}) {
  return (
    <div
      id={toolbarId}
      className="ql-toolbar ql-snow sd-rich-text-toolbar"
      aria-label="Formatting"
    >
      <span className="ql-formats sd-rich-text-toolbar__row">
        <select className="ql-header" defaultValue="">
          <option value="1">Heading 1</option>
          <option value="2">Heading 2</option>
          <option value="">Normal</option>
        </select>
        <select className="ql-font" defaultValue="sans-serif">
          <option value="sans-serif">Sans Serif</option>
          <option value="serif">Serif</option>
          <option value="monospace">Monospace</option>
        </select>
        <select className="ql-size" defaultValue="">
          <option value="small">Small</option>
          <option value="">Normal</option>
          <option value="large">Large</option>
          <option value="huge">Huge</option>
        </select>
      </span>
      <span className="ql-formats sd-rich-text-toolbar__row">
        <button type="button" className="ql-list" value="ordered" aria-label="Ordered list" />
        <button type="button" className="ql-list" value="bullet" aria-label="Bullet list" />
        <select className="ql-align" defaultValue="" aria-label="Text align">
          <option value="" />
          <option value="center" />
          <option value="right" />
          <option value="justify" />
        </select>
        <button type="button" className="ql-bold" aria-label="Bold" />
        <button type="button" className="ql-italic" aria-label="Italic" />
        <button type="button" className="ql-underline" aria-label="Underline" />
        <button type="button" className="ql-link" aria-label="Link" />
      </span>
      <span className="ql-formats sd-rich-text-toolbar__row sd-rich-text-toolbar__row--last">
        <select className="ql-color" defaultValue="" aria-label="Text color" />
        <select className="ql-background" defaultValue="" aria-label="Highlight color" />
        <button type="button" className="ql-blockquote" aria-label="Blockquote" />
      </span>
    </div>
  );
});

export function TicketDescriptionRichTextEditor({
  id,
  value,
  onChange,
  placeholder = "Write something…",
  maxPlainLength = 2000,
  error = false,
  disabled = false,
  className,
}: {
  id?: string;
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  maxPlainLength?: number;
  error?: boolean;
  disabled?: boolean;
  className?: string;
}) {
  const reactId = useId();
  const toolbarId = `sd-quill-toolbar-${reactId.replace(/:/g, "")}`;
  const lastValidValue = useRef(value);
  const quillRef = useRef<ReactQuill | null>(null);

  useEffect(() => {
    lastValidValue.current = value;
  }, [value]);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: `#${toolbarId}`,
      },
    }),
    [toolbarId]
  );

  const formats = useMemo(
    () => [
      "header",
      "font",
      "size",
      "bold",
      "italic",
      "underline",
      "list",
      "bullet",
      "align",
      "link",
      "color",
      "background",
      "blockquote",
    ],
    []
  );

  const handleChange = (
    content: string,
    _delta: unknown,
    source: string
  ) => {
    if (source !== "user") return;

    const withoutImages = stripInlineImages(content);
    const plainLen = richTextPlainTextLength(withoutImages);
    if (plainLen > maxPlainLength) {
      const editor = quillRef.current?.getEditor();
      if (editor) {
        editor.clipboard.dangerouslyPasteHTML(lastValidValue.current, "silent");
      }
      return;
    }

    lastValidValue.current = withoutImages;
    onChange(withoutImages);
  };

  return (
    <div
      id={id}
      className={cn(
        "sd-rich-text-editor ql-snow",
        error && "sd-input-error",
        disabled && "sd-rich-text-editor--disabled",
        className
      )}
    >
      <TicketDescriptionEditorToolbar toolbarId={toolbarId} />
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={handleChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
        readOnly={disabled}
      />
    </div>
  );
}
